'use client';

import { useCallback, useEffect, useState } from 'react';
import Link from 'next/link';
import { Check, X, Users, ListPlus, TrendingUp, HelpCircle } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { useLang } from '@/lib/i18n/LanguageContext';
import { track } from '@/lib/analytics';

// "First steps" checklist for new accounts (2026-09-05).
// Why: sign-ups were stopping right after registration — of the last 10 accounts only 2 posted
// a signal and 2 followed anyone. Most arrivals are beginners, so the card says out loud that
// nothing here costs money and turns the empty first session into four concrete actions.
// Progress comes from real rows (/api/onboarding/status), never from a local flag, except the
// "how it works" step which is a read-only modal. Shown for accounts younger than 30 days
// until every step is done; the user can hide it (per browser).

type Suggested = {
  username: string;
  displayName: string;
  image: string | null;
  stmScore: number;
  tier: string;
  closedTrades: number;
};

type Status = {
  createdAt: string;
  following: number;
  watchlist: number;
  signals: number;
  suggested: Suggested[];
};

const DISMISS_KEY = 'smt_first_steps_dismissed';
const HOW_KEY = 'smt_first_steps_how_seen';
const MAX_AGE_DAYS = 30;
const FOLLOW_TARGET = 3;

interface Props {
  onPostTrade: () => void;
  onHowItWorks: () => void;
  /** Bumps when a trade was posted from this page so the card refreshes. */
  refreshKey?: number;
}

export default function FirstStepsCard({ onPostTrade, onHowItWorks, refreshKey = 0 }: Props) {
  const { t } = useLang();
  const [status, setStatus] = useState<Status | null>(null);
  const [hidden, setHidden] = useState(true);
  const [howSeen, setHowSeen] = useState(false);
  const [followed, setFollowed] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const supabase = createClient();
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const res = await fetch('/api/onboarding/status', {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (!res.ok) return;
      const data: Status = await res.json();
      setStatus(data);
    } catch { /* card is optional */ }
  }, []);

  useEffect(() => {
    try {
      setHidden(localStorage.getItem(DISMISS_KEY) === '1');
      setHowSeen(localStorage.getItem(HOW_KEY) === '1');
    } catch { setHidden(false); }
    load();
  }, [load, refreshKey]);

  if (hidden || !status) return null;

  const ageDays = (Date.now() - new Date(status.createdAt).getTime()) / 864e5;
  if (ageDays > MAX_AGE_DAYS) return null;

  const followingCount = status.following + followed.size;
  const steps = [
    { key: 'follow', done: followingCount >= FOLLOW_TARGET },
    { key: 'watchlist', done: status.watchlist > 0 },
    { key: 'signal', done: status.signals > 0 },
    { key: 'how', done: howSeen },
  ];
  const doneCount = steps.filter((s) => s.done).length;
  if (doneCount === steps.length) return null;

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* ignore */ }
    setHidden(true);
    track('first_steps_dismiss', { done: doneCount });
  };

  const markHowSeen = () => {
    try { localStorage.setItem(HOW_KEY, '1'); } catch { /* ignore */ }
    setHowSeen(true);
    track('first_steps_how_it_works');
    onHowItWorks();
  };

  const follow = async (username: string) => {
    if (busy) return;
    setBusy(username);
    try {
      const supabase = createClient();
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`/api/users/${encodeURIComponent(username)}/follow`, {
        method: 'POST',
        headers: session ? { Authorization: `Bearer ${session.access_token}` } : {},
      });
      const json = await res.json().catch(() => ({}));
      if (res.ok && json.following) {
        setFollowed((prev) => new Set(prev).add(username));
        track('first_steps_follow', { username });
      }
    } catch { /* ignore */ } finally {
      setBusy(null);
    }
  };

  const StepHead = ({ n, done, title, desc }: { n: number; done: boolean; title: string; desc: string }) => (
    <div className="flex items-start gap-3">
      <div
        className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-xs font-bold"
        style={done
          ? { background: 'var(--accent)', color: 'var(--bg-base)' }
          : { background: 'var(--bg-sunken)', color: 'var(--text-muted)', border: '1px solid var(--border)' }}
      >
        {done ? <Check size={14} /> : n}
      </div>
      <div className="min-w-0">
        <div className="text-sm font-semibold" style={{ color: done ? 'var(--text-muted)' : 'var(--text-primary)', textDecoration: done ? 'line-through' : 'none' }}>
          {title}
        </div>
        {!done && <div className="text-xs mt-0.5" style={{ color: 'var(--text-secondary)' }}>{desc}</div>}
      </div>
    </div>
  );

  return (
    <section
      className="rounded-2xl p-4 sm:p-5"
      style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', boxShadow: '0 2px 12px rgba(28,43,38,0.06)' }}
      aria-label={t('First steps')}
    >
      <div className="flex items-center justify-between gap-3 mb-1">
        <h2 className="text-base font-bold" style={{ color: 'var(--text-primary)' }}>
          {t('First steps')}{' '}
          <span className="text-xs font-semibold ml-1 px-2 py-0.5 rounded-full" style={{ background: 'var(--accent-muted, var(--bg-sunken))', color: 'var(--accent)' }}>
            {doneCount}/{steps.length} {t('done')}
          </span>
        </h2>
        <button onClick={dismiss} className="p-1.5 rounded-lg" title={t('Dismiss')} aria-label={t('Dismiss')} style={{ color: 'var(--text-muted)' }}>
          <X size={16} />
        </button>
      </div>
      <p className="text-xs mb-4" style={{ color: 'var(--text-secondary)' }}>
        {t('No real money, no subscription — everything here is a simulated $10,000 account.')}
      </p>

      <div className="h-1.5 rounded-full mb-4" style={{ background: 'var(--bg-sunken)' }}>
        <div className="h-1.5 rounded-full transition-all" style={{ width: `${(doneCount / steps.length) * 100}%`, background: 'var(--accent)' }} />
      </div>

      <div className="flex flex-col gap-4">
        {/* 1 — follow traders */}
        <div>
          <StepHead n={1} done={steps[0].done} title={`${t('Follow 3 traders')} (${Math.min(followingCount, FOLLOW_TARGET)}/${FOLLOW_TARGET})`} desc={t('Their signals land in your Following tab and notifications.')} />
          {!steps[0].done && status.suggested.length > 0 && (
            <div className="mt-3 ml-10 flex flex-col gap-2">
              {status.suggested.map((s) => {
                const isFollowed = followed.has(s.username);
                return (
                  <div key={s.username} className="flex items-center gap-3">
                    <Link href={`/profile/${s.username}`} className="flex items-center gap-2 min-w-0 flex-1">
                      <div className="w-8 h-8 rounded-full overflow-hidden flex items-center justify-center text-xs font-bold shrink-0" style={{ background: 'var(--accent)', color: 'var(--bg-base)' }}>
                        {s.image ? <img src={s.image} alt="" className="w-full h-full object-cover" /> : s.displayName[0]?.toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold truncate" style={{ color: 'var(--text-primary)' }}>{s.displayName}</div>
                        <div className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
                          STM {s.stmScore.toFixed(1)} · {s.closedTrades} {t('closed trades')}
                        </div>
                      </div>
                    </Link>
                    <button
                      onClick={() => follow(s.username)}
                      disabled={isFollowed || busy === s.username}
                      className="px-3 py-1.5 rounded-lg text-xs font-bold shrink-0 transition-all"
                      style={isFollowed
                        ? { background: 'var(--bg-sunken)', color: 'var(--text-muted)', border: '1px solid var(--border)' }
                        : { background: 'var(--accent)', color: 'var(--bg-base)' }}
                    >
                      {isFollowed ? t('Following') : t('Follow')}
                    </button>
                  </div>
                );
              })}
              <Link href="/discover" className="text-xs font-semibold mt-1 inline-flex items-center gap-1" style={{ color: 'var(--accent)' }}>
                <Users size={12} /> {t('See all traders')}
              </Link>
            </div>
          )}
        </div>

        {/* 2 — watchlist */}
        <div>
          <StepHead n={2} done={steps[1].done} title={t('Add an instrument to your watchlist')} desc={t('Gold, EURUSD, BTC — pick what you already look at every day.')} />
          {!steps[1].done && (
            <div className="mt-2 ml-10">
              <Link href="/watchlist" onClick={() => track('first_steps_watchlist')} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold" style={{ background: 'var(--bg-sunken)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}>
                <ListPlus size={13} /> {t('Open watchlist')}
              </Link>
            </div>
          )}
        </div>

        {/* 3 — first signal */}
        <div>
          <StepHead n={3} done={steps[2].done} title={t('Post your first signal')} desc={t('Entry, stop and target — the market settles it, not you. A wrong call costs nothing.')} />
          {!steps[2].done && (
            <div className="mt-2 ml-10">
              <button onClick={() => { track('first_steps_post_signal'); onPostTrade(); }} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold" style={{ background: 'var(--accent)', color: 'var(--bg-base)' }}>
                <TrendingUp size={13} /> {t('Post a signal')}
              </button>
            </div>
          )}
        </div>

        {/* 4 — how the score works */}
        <div>
          <StepHead n={4} done={steps[3].done} title={t('Learn how the score works')} desc={t('2 minutes: what counts, what does not, how you get on the leaderboard.')} />
          {!steps[3].done && (
            <div className="mt-2 ml-10">
              <button onClick={markHowSeen} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold" style={{ background: 'var(--bg-sunken)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}>
                <HelpCircle size={13} /> {t('Show me')}
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
